import { getAccessToken, refreshAccessToken } from '@/api/auth';

const graphqlUri =
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined) ?? '/graphql';

/** Absolute download URL: relative `/export/...` paths use the GraphQL API origin. */
function resolveExportUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  try {
    const api = new URL(graphqlUri, window.location.origin);
    return new URL(url, api.origin).toString();
  } catch {
    return url;
  }
}

/**
 * Downloads a CSV export URL with the current Bearer access token.
 * Retries once after a refresh on 401.
 */
export async function downloadAuthenticatedCsv(url: string): Promise<void> {
  const absoluteUrl = resolveExportUrl(url);

  const attempt = async (token: string | null): Promise<Response> => {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(absoluteUrl, { headers, credentials: 'omit' });
  };

  let token = getAccessToken();
  let res = await attempt(token);

  if (res.status === 401) {
    token = await refreshAccessToken();
    if (!token) {
      throw new Error('Session expired. Sign in again to download the export.');
    }
    res = await attempt(token);
  }

  if (!res.ok) {
    const text = (await res.text()).trim();
    throw new Error(text || `Export download failed (${res.status}).`);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] ?? 'export.csv';

  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
